import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import type { CameraMapPin, MapSettings } from '@/types'
import { cn } from '@/lib/utils'
import { countByStatus, groupsOf, type MapPinFilter, type PinStatusFilter } from '@/lib/mapPinFilter'
import { DEFAULT_MAP_SETTINGS, OFFLINE_RED, PIN_COLOUR_HEX } from '@/lib/mapSettings'

// #247 — status chips with counts, a group select and a search box above the fleet map.
const STATUSES: PinStatusFilter[] = ['all', 'online', 'offline', 'unknown']
// #282 — the chips double as the legend: the pin's mark (see CameraPinSvg).
// #286 — every pin is one colour; the offline chip's dot carries the small red badge the pin carries.
// #287 — the colour and the offline mark come from the company's map settings (passed in by the page).
const WITH_DOT: PinStatusFilter[] = ['online', 'offline', 'unknown']

export function FleetMapFilters({ pins, value, onChange, settings = DEFAULT_MAP_SETTINGS }: {
  pins: CameraMapPin[]
  value: MapPinFilter
  onChange: (next: MapPinFilter) => void
  settings?: MapSettings
}) {
  const { t } = useTranslation('cameras')
  const counts = countByStatus(pins)
  const groups = groupsOf(pins)
  const pinColour = PIN_COLOUR_HEX[settings.pin_colour]
  const dotColour = (s: PinStatusFilter) => (s === 'offline' && settings.offline_mark === 'red_pin' ? OFFLINE_RED : pinColour)
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {STATUSES.map((s) => (
        <button
          key={s}
          type="button"
          aria-pressed={value.status === s}
          onClick={() => onChange({ ...value, status: s })}
          className={cn(
            'inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs font-medium transition-colors',
            value.status === s ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-muted-foreground hover:text-foreground',
          )}
        >
          {WITH_DOT.includes(s) && (
            // the pin's dark outline keeps the dot visible on the selected chip, which is the brand amber too
            <span aria-hidden data-status-dot={s} className="relative h-2 w-2 shrink-0 rounded-full ring-1 ring-slate-800/70" style={{ backgroundColor: dotColour(s) }}>
              {s === 'offline' && settings.offline_mark === 'red_dot' && (
                <span data-status-badge className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-red-500 ring-1 ring-background" />
              )}
            </span>
          )}
          {t(`map.filter.${s}`)} <span className="tabular-nums opacity-80">{counts[s]}</span>
        </button>
      ))}
      {groups.length > 0 && (
        <select
          aria-label={t('map.filter.allGroups')}
          value={value.group ?? ''}
          onChange={(e) => onChange({ ...value, group: e.target.value || null })}
          className="h-7 rounded-md border border-border bg-background px-2 text-xs text-foreground"
        >
          <option value="">{t('map.filter.allGroups')}</option>
          {groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      )}
      <label className="relative ml-auto flex items-center">
        <Search className="pointer-events-none absolute left-2 h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <input
          type="search"
          aria-label={t('map.search')}
          placeholder={t('map.search')}
          value={value.query}
          onChange={(e) => onChange({ ...value, query: e.target.value })}
          className="h-7 w-40 rounded-md border border-border bg-background pl-7 pr-2 text-xs text-foreground placeholder:text-muted-foreground"
        />
      </label>
    </div>
  )
}
